import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import { useLocation, useParams } from "react-router-dom";
import type { CaseFileLite, ResponsiveFile } from "@/briefpoint-client";
import { ToggleableFeature } from "@/briefpoint-client";
import { toast } from "@/components/ui/toast";
import { useDocumentApi, useProductionSetApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { RESPONSIVE_FILE_ITEM_CHECKBOX } from "@/lib/constants";
import {
  getProductionSetOptions,
  getResponsiveFilesForInterrogatoryOptions,
} from "@/lib/query-options";
import { userCanUseFeature } from "@/lib/utils";
import styles from "./ResponsiveFileItem.module.scss";

interface Props {
  file: CaseFileLite;
  responsiveFile?: ResponsiveFile;
  selectedFile: string | null;
  onSelect: (fileId: string) => void;
}

export default function ResponsiveFileItem(props: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const documentApi = useDocumentApi();
  const productionSetApi = useProductionSetApi();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const interrogatoryIdParam = Number(searchParams.get("interrogatoryId"));
  const urlParams = useParams<{
    documentId: string;
  }>();

  const userCanUseDocHandling = user
    ? userCanUseFeature(user, ToggleableFeature.Autodoc)
    : false;
  const [isChecked, setIsChecked] = useState(
    !!props.responsiveFile && !props.responsiveFile.isWithheld,
  );

  const responsiveFilesQueryOptions = getResponsiveFilesForInterrogatoryOptions(
    urlParams.documentId,
    interrogatoryIdParam,
    documentApi,
    false,
    userCanUseDocHandling,
  );

  // Update checked state when responsive file changes
  useEffect(() => {
    setIsChecked(!!props.responsiveFile && !props.responsiveFile.isWithheld);
  }, [props.responsiveFile]);

  /* get current production set, use bates number for creating new production set */
  const batesNumberQuery = useQuery({
    ...getProductionSetOptions(
      urlParams.documentId,
      productionSetApi,
      userCanUseDocHandling,
    ),
  });

  const newProductionSetCreateMutation = useMutation({
    /* create new production set with bates number from current production set */
    mutationFn: async () => {
      return await productionSetApi.productionSetCreate({
        productionSetCreate: {
          documentId: urlParams.documentId,
          caseId: props.file.caseId || "",
          batesNumberPrefix:
            batesNumberQuery.data?.[0]?.batesNumberPrefix || "",
          batesNumberStart:
            batesNumberQuery.data?.[0]?.batesNumberStart?.toString() || "1",
        },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries(responsiveFilesQueryOptions);
    },
  });

  // Shared mutation configuration
  const mutationConfig = (
    mutationFn: (isWithheld: boolean) => Promise<void>,
  ) => ({
    mutationFn,
    onMutate: (isWithheld: boolean) => {
      const previousData = queryClient.getQueryData(
        responsiveFilesQueryOptions.queryKey,
      );

      // Optimistically update the file
      queryClient.setQueryData(responsiveFilesQueryOptions.queryKey, (old) => {
        if (!old) return old;

        // If the responsive file exists, update it
        if (props.responsiveFile) {
          return old.map((file: ResponsiveFile) => {
            if (file.id === props.responsiveFile?.id) {
              return {
                ...file,
                isWithheld,
              };
            }
            return file;
          });
        }

        // If the responsive file doesn't exist, create a fake one
        return [
          ...old,
          {
            id: props.file.id,
            isWithheld,
            caseFileId: props.file.id,
            interrogatoryId: interrogatoryIdParam,
            documentId: urlParams.documentId,
            creationDate: new Date(),
            isVerified: false,
          },
        ];
      });

      return { previousData };
    },
    onError: (
      error: Error,
      _: unknown,
      context: { previousData?: ResponsiveFile[] } | undefined,
    ) => {
      // Revert the optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(
          responsiveFilesQueryOptions.queryKey,
          context.previousData,
        );
      }

      toast.error(error.message, { description: "Error updating document" });
    },
    onSettled: () => {
      // create a new production set if there is a first production set and it has been downloaded
      if (
        batesNumberQuery.data?.length &&
        batesNumberQuery.data?.[0]?.downloadedDate
      ) {
        newProductionSetCreateMutation.mutate();
      }
    },
  });

  const createResponsiveFileMutation = useMutation(
    mutationConfig(async (isWithheld: boolean) => {
      await documentApi.documentAddResponsiveFileToDocument({
        documentId: urlParams.documentId,
        interrogatoryId: interrogatoryIdParam,
        postResponsiveFileRequest: {
          caseFileId: props.file.id,
          isWithheld,
        },
      });
    }),
  );

  const updateResponsiveFileMutation = useMutation(
    mutationConfig(async (isWithheld: boolean) => {
      await documentApi.documentUpdateResponsiveFile({
        responsiveFileId: props.responsiveFile?.id || "",
        documentId: urlParams.documentId,
        putResponsiveFileRequest: {
          isWithheld,
        },
      });
    }),
  );

  const handleCheckboxChange = () => {
    const newIsChecked = !isChecked;
    setIsChecked(newIsChecked);

    if (props.responsiveFile) {
      updateResponsiveFileMutation.mutate(!newIsChecked);
    } else {
      createResponsiveFileMutation.mutate(!newIsChecked);
    }
  };

  return (
    <div
      className={clsx(
        styles.file,
        props.selectedFile === props.file.id && styles.selected,
      )}
      key={props.file.id}
    >
      <input
        type="checkbox"
        className={`form-check-input m-0 ms-2 ${RESPONSIVE_FILE_ITEM_CHECKBOX}`}
        onChange={handleCheckboxChange}
        checked={isChecked}
        disabled={
          createResponsiveFileMutation.isPending ||
          updateResponsiveFileMutation.isPending
        }
      />
      <Button
        variant="link"
        className={`ps-2 text-decoration-none ${styles.item}`}
        onClick={() => {
          props.onSelect(props.file.id);
        }}
      >
        <div className={styles.name} title={props.file.name}>
          {props.file.name}
        </div>
      </Button>
    </div>
  );
}
